import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { User, Mail, Lock, Phone, Globe, ArrowRight, Eye, EyeOff } from "lucide-react";

function Field({ icon: Icon, ...props }) {
  return (
    <div className="flex items-center rounded-xl border border-border bg-white px-3 focus-within:ring-2 focus-within:ring-sage-300">
      <Icon className="w-4 h-4 text-ink-mute" />
      <input {...props} className="flex-1 px-3 py-3 bg-transparent focus:outline-none" />
    </div>
  );
}

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", country: "" });
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      toast.error("Mot de passe : 6 caractères minimum");
      return;
    }
    setLoading(true);
    const res = await register(form);
    setLoading(false);
    if (res.ok) {
      toast.success("Bienvenue dans l'espace Sama Écoute");
      navigate("/dashboard");
    } else {
      toast.error(res.error || "Inscription impossible");
    }
  };

  return (
    <div className="min-h-[calc(100vh-12rem)] flex items-center bg-bone">
      <div className="container-jamm grid md:grid-cols-2 gap-12 items-center py-16">
        <div className="hidden md:block">
          <div className="overline">Bienvenue</div>
          <h1 className="font-display text-5xl text-ink mt-4 leading-tight">Un espace, juste<br /><span className="italic text-sage-600">pour vous.</span></h1>
          <p className="lead mt-5 max-w-md">Créez votre compte en moins d'une minute. Réservez, échangez, restez vous-même — anonyme si vous le souhaitez.</p>
          <img src="https://images.unsplash.com/photo-1730575959795-c55aba81e703?crop=entropy&cs=srgb&fm=jpg&w=900&q=80" alt="" className="mt-10 rounded-3xl w-full h-72 object-cover" />
        </div>

        <form onSubmit={submit} className="card-soft max-w-md w-full mx-auto md:mx-0" data-testid="register-form">
          <h2 className="font-display text-3xl text-ink">Créer un compte</h2>
          <p className="text-ink-soft mt-1 text-sm">Quelques informations et c'est parti.</p>

          <label className="block mt-6 text-sm text-ink-soft">Nom complet</label>
          <Field icon={User} required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Votre nom" data-testid="register-name" />

          <label className="block mt-4 text-sm text-ink-soft">Email</label>
          <Field icon={Mail} required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="vous@email.com" data-testid="register-email" />

          <label className="block mt-4 text-sm text-ink-soft">Mot de passe (6+ caractères)</label>
          <div className="flex items-center rounded-xl border border-border bg-white px-3 focus-within:ring-2 focus-within:ring-sage-300">
            <Lock className="w-4 h-4 text-ink-mute" />
            <input
              required type={showPwd ? "text" : "password"} value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="flex-1 px-3 py-3 bg-transparent focus:outline-none"
              placeholder="••••••••"
              autoComplete="new-password"
              data-testid="register-password"
            />
            <button
              type="button"
              onClick={() => setShowPwd((v) => !v)}
              className="p-2 -mr-2 text-ink-mute hover:text-ink-soft transition rounded-lg"
              aria-label={showPwd ? "Masquer le mot de passe" : "Afficher le mot de passe"}
              tabIndex={-1}
              data-testid="register-toggle-password"
            >
              {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3 mt-4">
            <div>
              <label className="block text-sm text-ink-soft">Téléphone</label>
              <Field icon={Phone} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+221…" data-testid="register-phone" />
            </div>
            <div>
              <label className="block text-sm text-ink-soft">Pays</label>
              <Field icon={Globe} value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} placeholder="Sénégal" data-testid="register-country" />
            </div>
          </div>

          <button type="submit" disabled={loading} className="btn-primary w-full justify-center mt-6" data-testid="register-submit">
            {loading ? "Création…" : (<>Créer mon compte <ArrowRight className="w-4 h-4" /></>)}
          </button>

          <div className="text-center mt-5 text-sm text-ink-soft">
            Vous avez déjà un compte ? <Link to="/login" className="text-sage-600 font-medium hover:underline" data-testid="register-to-login">Se connecter</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
